import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";
import { authApi } from "../../../auth/api";
import { getInviteURL } from "../../../base/connection/functions";
import Icon from "../../../base/icons/components/Icon";
import { IconCheck, IconCloseLarge, IconCopy, IconPlus, IconSend } from "../../../base/icons/svg";
import { copyText } from "../../../base/util/copyText.web";
import { getDecodedURI } from "../../../base/util/uri";
import { toggleShareDialog } from "../../actions";
const useStyles = makeStyles()(() => {
    return {
        overlay: {
            position: "fixed",
            bottom: "100px",
            zIndex: 1000,
            animation: "slideInRight 0.3s ease-out",
            '[dir="rtl"] &': {
                left: "20px",
                right: "auto",
            },
            '[dir="ltr"] &, &': {
                right: "20px",
            },
            "@media (max-width: 768px)": {
                bottom: 0,
                right: 0,
                left: 0,
                top: 0,
                display: "flex",
                alignItems: "flex-end",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                animation: "fadeIn 0.2s ease-out",
            },
        },
        popup: {
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            width: "380px",
            maxWidth: "calc(100vw - 40px)",
            overflow: "hidden",
            "@media (max-width: 768px)": {
                width: "100%",
                maxWidth: "100%",
                borderRadius: "16px 16px 0 0",
                animation: "slideUp 0.3s ease-out",
            },
        },
        header: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid #eee",
            direction: "rtl",
        },
        title: {
            fontSize: "1rem",
            fontWeight: 600,
            color: "#000",
            margin: 0,
        },
        closeButton: {
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            transition: "background 0.2s ease",
            "&:hover": {
                background: "#f0f0f0",
            },
            "& svg": {
                fill: "#666",
                width: "16px",
                height: "16px",
            },
        },
        content: {
            padding: "20px",
            textAlign: "right",
        },
        description: {
            fontSize: "0.875rem",
            color: "#666",
            marginBottom: "16px",
            lineHeight: 1.5,
        },
        inputWrapper: {
            display: "flex",
            alignItems: "center",
            border: "1px solid #ddd",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#f9f9f9",
        },
        linkInput: {
            flex: 1,
            border: "none",
            padding: "12px 16px",
            fontSize: "0.875rem",
            color: "#333",
            backgroundColor: "transparent",
            outline: "none",
            direction: "ltr",
            textAlign: "left",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "'Times New Roman', serif",
            "& *": {
                fontFamily: "inherit",
            },
        },
        roomName: {
            fontWeight: 700,
            color: "#000",
        },
        copyButton: {
            background: "transparent",
            border: "none",
            padding: "12px 16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            transition: "opacity 0.2s ease",
            "&:hover": {
                opacity: 0.7,
            },
            "&.copied": {
                "& svg": {
                    fill: "#28a745",
                },
            },
            "& svg": {
                fill: "#0063e4",
                width: "18px",
                height: "18px",
            },
        },
        sendInviteButton: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            marginTop: "12px",
            padding: "10px 16px",
            background: "#0063e4",
            border: "none",
            borderRadius: "8px",
            color: "#fff",
            fontSize: "0.875rem",
            fontWeight: 500,
            cursor: "pointer",
            transition: "background 0.2s ease",
            "&:hover": {
                background: "#0052cc",
            },
            "& svg": {
                fill: "#fff",
                width: "18px",
                height: "18px",
            },
        },
        modalOverlay: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            animation: "fadeIn 0.2s ease-out",
            "@media (max-width: 768px)": {
                alignItems: "flex-end",
            },
        },
        modal: {
            backgroundColor: "#fff",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            width: "400px",
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "90vh",
            overflow: "auto",
            animation: "modalSlideUp 0.3s ease-out",
            direction: "rtl",
            "@media (max-width: 768px)": {
                width: "100%",
                maxWidth: "100%",
                borderRadius: "16px 16px 0 0",
                animation: "slideUp 0.3s ease-out",
            },
        },
        modalHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid #eee",
        },
        modalTitle: {
            fontSize: "1rem",
            fontWeight: 600,
            color: "#000",
            margin: 0,
        },
        modalContent: {
            padding: "20px",
        },
        addPhoneRow: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
        },
        phoneInput: {
            flex: 1,
            border: "1px solid #ddd",
            borderRadius: "8px",
            padding: "10px 14px",
            fontSize: "0.9rem",
            outline: "none",
            direction: "ltr",
            textAlign: "left",
            transition: "border-color 0.2s ease",
            fontFamily: "inherit",
            "&:focus": {
                borderColor: "#0063e4",
                boxShadow: "0 0 0 2px rgba(0, 99, 228, 0.1)",
            },
            "&::placeholder": {
                color: "#bbb",
                fontSize: "0.85rem",
            },
        },
        addButton: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            minWidth: "40px",
            border: "none",
            borderRadius: "8px",
            background: "#0063e4",
            cursor: "pointer",
            transition: "background 0.2s ease",
            "&:hover": {
                background: "#0052cc",
            },
            "&:disabled": {
                background: "#ccc",
                cursor: "not-allowed",
            },
            "& svg": {
                fill: "#fff",
            },
        },
        badgeList: {
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            marginTop: "14px",
            minHeight: "20px",
        },
        badge: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "6px",
            padding: "6px 8px 6px 12px",
            backgroundColor: "#f0f6ff",
            border: "1px solid #d0e2ff",
            borderRadius: "20px",
            fontSize: "0.85rem",
            color: "#333",
            direction: "ltr",
            animation: "badgePop 0.2s ease-out",
        },
        badgeNumber: {
            fontWeight: 500,
            fontFamily: "monospace",
        },
        badgeDelete: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            border: "none",
            borderRadius: "50%",
            background: "transparent",
            cursor: "pointer",
            padding: 0,
            transition: "background 0.15s ease",
            "&:hover": {
                background: "rgba(0, 0, 0, 0.08)",
            },
            "& svg": {
                fill: "#dc3545 !important",
                width: "14px",
                height: "14px",
            },
        },
        emptyBadgeHint: {
            fontSize: "0.8rem",
            color: "#aaa",
            fontStyle: "italic",
            whiteSpace: "nowrap",
        },
        errorText: {
            color: "#dc3545",
            fontSize: "0.8rem",
            marginTop: "10px",
            textAlign: "right",
        },
        modalFooter: {
            padding: "0 20px 20px",
        },
        sendButton: {
            width: "100%",
            padding: "12px",
            border: "none",
            borderRadius: "8px",
            fontSize: "0.9rem",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            background: "#0063e4",
            color: "#fff",
            "&:hover": {
                background: "#0052cc",
            },
            "&:disabled": {
                background: "#ccc",
                cursor: "not-allowed",
            },
            "&.success": {
                background: "#28a745",
            },
            "&.error": {
                background: "#dc3545",
            },
            "& svg": {
                fill: "#fff",
                width: "18px",
                height: "18px",
            },
        },
        drawerHandle: {
            display: "none",
            width: "40px",
            height: "4px",
            backgroundColor: "#ddd",
            borderRadius: "2px",
            margin: "8px auto 0",
            "@media (max-width: 768px)": {
                display: "block",
            },
        },
    };
});
const globalStyles = `
    @keyframes slideUp {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
    }
    @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
    }
    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
    @keyframes modalSlideUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
    }
    @keyframes badgePop {
        from { transform: scale(0.8); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
    }
`;
if (typeof document !== "undefined") {
    const styleId = "share-meeting-popup-styles";
    if (!document.getElementById(styleId)) {
        const styleElement = document.createElement("style");
        styleElement.id = styleId;
        styleElement.textContent = globalStyles;
        document.head.appendChild(styleElement);
    }
}
// eslint-disable-next-line react/no-multi-comp
const PhoneBadge = ({ classes, index, onRemove, phone }) => {
    const _handleRemove = useCallback(() => {
        onRemove(index);
    }, [index, onRemove]);
    return (React.createElement("div", { className: classes.badge },
        React.createElement("span", { className: classes.badgeNumber }, phone),
        React.createElement("button", { className: classes.badgeDelete, onClick: _handleRemove, type: "button" },
            React.createElement(Icon, { color: "#dc3545", size: 14, src: IconCloseLarge }))));
};
// eslint-disable-next-line react/no-multi-comp
const ShareMeetingPopup = () => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const shareDialogVisible = useSelector((state) => state["features/share-room"]?.shareDialogVisible ?? false);
    const isFirstTimeOpen = useSelector((state) => state["features/share-room"]?.isFirstTimeOpen ?? false);
    const inviteURL = useSelector((state) => getInviteURL(state));
    const [copied, setCopied] = useState(false);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [currentPhone, setCurrentPhone] = useState("");
    const [sendStatus, setSendStatus] = useState("idle");
    const [validationError, setValidationError] = useState("");
    const phoneInputRef = useRef(null);
    const handleClose = useCallback(() => {
        dispatch(toggleShareDialog(false));
    }, [dispatch]);
    // Auto-close after 5 seconds only on first time open (for moderator)
    useEffect(() => {
        if (shareDialogVisible && isFirstTimeOpen) {
            const timer = setTimeout(() => {
                handleClose();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [shareDialogVisible, isFirstTimeOpen, handleClose]);
    const handleCopy = useCallback(async () => {
        const success = await copyText(inviteURL);
        if (success) {
            setCopied(true);
            const isMobile = window.innerWidth <= 768;
            setTimeout(() => {
                setCopied(false);
                if (isMobile) {
                    dispatch(toggleShareDialog(false));
                }
            }, 1000);
        }
    }, [inviteURL, dispatch]);
    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget && window.innerWidth <= 768) {
            handleClose();
        }
    }, [handleClose]);
    const handleOpenInviteModal = useCallback(() => {
        setInviteModalOpen(true);
        setPhoneNumbers([]);
        setCurrentPhone("");
        setSendStatus("idle");
        setValidationError("");
        setTimeout(() => {
            phoneInputRef.current?.focus();
        }, 100);
    }, []);
    const handleCloseInviteModal = useCallback(() => {
        setInviteModalOpen(false);
        setSendStatus("idle");
        setValidationError("");
    }, []);
    const handleModalOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            handleCloseInviteModal();
        }
    }, [handleCloseInviteModal]);
    const handleCurrentPhoneChange = useCallback((e) => {
        const cleaned = e.target.value.replace(/[^\d]/g, "").slice(0, 11);
        setCurrentPhone(cleaned);
        setValidationError("");
    }, []);
    const maxPhones = 2;
    const handleAddPhone = useCallback(() => {
        const trimmed = currentPhone.trim();
        if (trimmed.length === 0 || phoneNumbers.length >= maxPhones) {
            return;
        }
        if (!/^09\d{9}$/.test(trimmed)) {
            setValidationError(t("shareMeeting.phoneInvalid"));
            return;
        }
        if (phoneNumbers.includes(trimmed)) {
            setValidationError(t("shareMeeting.phoneDuplicate"));
            return;
        }
        setPhoneNumbers((prev) => [...prev, trimmed]);
        setCurrentPhone("");
        setValidationError("");
        phoneInputRef.current?.focus();
    }, [currentPhone, phoneNumbers, t]);
    const handlePhoneKeyDown = useCallback((e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddPhone();
        }
    }, [handleAddPhone]);
    const handleRemovePhone = useCallback((index) => {
        setPhoneNumbers((prev) => prev.filter((_, i) => i !== index));
    }, []);
    const handleSendInvite = useCallback(async () => {
        if (phoneNumbers.length === 0) {
            setValidationError(t("shareMeeting.phoneRequired"));
            return;
        }
        setSendStatus("sending");
        try {
            const roomName = inviteURL.split("/").pop() || "";
            await authApi.sendInvite({
                phoneNumbers,
                roomName,
            });
            setSendStatus("sent");
            setTimeout(() => {
                handleCloseInviteModal();
            }, 1500);
        }
        catch {
            setSendStatus("error");
            setTimeout(() => {
                setSendStatus("idle");
            }, 2000);
        }
    }, [phoneNumbers, inviteURL, t, handleCloseInviteModal]);
    const formatForInput = (value) => {
        if (value.length > 4) {
            return value.slice(0, 4) + "-" + value.slice(4);
        }
        return value;
    };
    if (!shareDialogVisible) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classes.overlay, onClick: handleOverlayClick },
            React.createElement("div", { className: classes.popup },
                React.createElement("div", { className: classes.drawerHandle }),
                React.createElement("div", { className: classes.header },
                    React.createElement("h3", { className: classes.title }, t("shareMeeting.title")),
                    React.createElement("button", { className: classes.closeButton, onClick: handleClose, type: "button" },
                        React.createElement("svg", { fill: "none", height: "20", viewBox: "0 0 20 20", width: "20", xmlns: "http://www.w3.org/2000/svg" },
                            React.createElement("path", { d: "M15.5886 5.58958C15.914 5.26414 15.914 4.73651 15.5886 4.41107C15.2632 4.08563 14.7355 4.08563 14.4101 4.41107L9.99935 8.82181L5.5886 4.41107C5.26317 4.08563 4.73553 4.08563 4.41009 4.41107C4.08466 4.73651 4.08466 5.26414 4.41009 5.58958L8.82084 10.0003L4.41009 14.4111C4.08466 14.7365 4.08466 15.2641 4.41009 15.5896C4.73553 15.915 5.26317 15.915 5.5886 15.5896L9.99935 11.1788L14.4101 15.5896C14.7355 15.915 15.2632 15.915 15.5886 15.5896C15.914 15.2641 15.914 14.7365 15.5886 14.4111L11.1779 10.0003L15.5886 5.58958Z", fill: "black" })))),
                React.createElement("div", { className: classes.content },
                    React.createElement("p", { className: classes.description }, t("shareMeeting.description")),
                    React.createElement("div", { className: classes.inputWrapper },
                        React.createElement("div", { className: classes.linkInput }, (() => {
                            const url = getDecodedURI(inviteURL);
                            const lastSlash = url.lastIndexOf("/");
                            if (lastSlash === -1) {
                                return React.createElement("span", { className: classes.roomName }, url);
                            }
                            const base = url.substring(0, lastSlash + 1);
                            const room = url.substring(lastSlash + 1);
                            return (React.createElement(React.Fragment, null,
                                base,
                                React.createElement("span", { className: classes.roomName }, room)));
                        })()),
                        React.createElement("button", { className: cx(classes.copyButton, copied ? "copied" : ""), onClick: handleCopy, type: "button" },
                            React.createElement(Icon, { color: copied ? "#28a745" : "#0063e4", size: 16, src: copied ? IconCheck : IconCopy })))))),
        inviteModalOpen && (React.createElement("div", { className: classes.modalOverlay, onClick: handleModalOverlayClick },
            React.createElement("div", { className: classes.modal },
                React.createElement("div", { className: classes.drawerHandle }),
                React.createElement("div", { className: classes.modalHeader },
                    React.createElement("h3", { className: classes.modalTitle }, t("shareMeeting.inviteModalTitle")),
                    React.createElement("button", { className: classes.closeButton, onClick: handleCloseInviteModal, type: "button" },
                        React.createElement("svg", { fill: "none", height: "20", viewBox: "0 0 20 20", width: "20", xmlns: "http://www.w3.org/2000/svg" },
                            React.createElement("path", { d: "M15.5886 5.58958C15.914 5.26414 15.914 4.73651 15.5886 4.41107C15.2632 4.08563 14.7355 4.08563 14.4101 4.41107L9.99935 8.82181L5.5886 4.41107C5.26317 4.08563 4.73553 4.08563 4.41009 4.41107C4.08466 4.73651 4.08466 5.26414 4.41009 5.58958L8.82084 10.0003L4.41009 14.4111C4.08466 14.7365 4.08466 15.2641 4.41009 15.5896C4.73553 15.915 5.26317 15.915 5.5886 15.5896L9.99935 11.1788L14.4101 15.5896C14.7355 15.915 15.2632 15.915 15.5886 15.5896C15.914 15.2641 15.914 14.7365 15.5886 14.4111L11.1779 10.0003L15.5886 5.58958Z", fill: "black" })))),
                React.createElement("div", { className: classes.modalContent },
                    React.createElement("div", { className: classes.addPhoneRow },
                        React.createElement("input", { className: classes.phoneInput, dir: "ltr", disabled: phoneNumbers.length >= maxPhones, inputMode: "tel", onChange: handleCurrentPhoneChange, onKeyDown: handlePhoneKeyDown, placeholder: phoneNumbers.length >= maxPhones
                                ? t("shareMeeting.maxPhones")
                                : t("shareMeeting.phonePlaceholder"), ref: phoneInputRef, type: "tel", value: formatForInput(currentPhone) }),
                        React.createElement("button", { className: classes.addButton, disabled: currentPhone.trim().length === 0 || phoneNumbers.length >= maxPhones, onClick: handleAddPhone, type: "button" },
                            React.createElement(Icon, { size: 20, src: IconPlus }))),
                    React.createElement("div", { className: classes.badgeList },
                        phoneNumbers.length === 0 && (React.createElement("span", { className: classes.emptyBadgeHint }, t("shareMeeting.addPhoneHint"))),
                        phoneNumbers.map((phone, idx) => (React.createElement(PhoneBadge, { classes: classes, index: idx, 
                            // eslint-disable-next-line react/no-array-index-key
                            key: `${phone}-${idx}`, onRemove: handleRemovePhone, phone: phone })))),
                    validationError && React.createElement("div", { className: classes.errorText }, validationError)),
                React.createElement("div", { className: classes.modalFooter },
                    React.createElement("button", { className: cx(classes.sendButton, sendStatus === "sent" ? "success" : "", sendStatus === "error" ? "error" : ""), disabled: sendStatus === "sending" || sendStatus === "sent" || phoneNumbers.length === 0, onClick: handleSendInvite, type: "button" },
                        sendStatus === "idle" && (React.createElement(React.Fragment, null,
                            React.createElement(Icon, { size: 18, src: IconSend }),
                            t("shareMeeting.send"))),
                        sendStatus === "sending" && t("shareMeeting.sending"),
                        sendStatus === "sent" && React.createElement(React.Fragment, null, t("shareMeeting.sent")),
                        sendStatus === "error" && t("shareMeeting.sendFailed"))))))));
};
export default ShareMeetingPopup;
